import { ColDef } from "ag-grid-community";
import { CustomCellRendererProps } from "ag-grid-react";
import { OpenInResourceExplorerButton } from "@/components/pages/dimensions/components/data-grid/common/OpenInResourceExplorerButton";
import {
  CategoryWithDimensionId,
  DimensionsWithChildren,
} from "@/store/dimensions/utils/types/common";
import { keyInsensitiveComparatorFn } from "@/components/pages/common/ag-grid/utils/helpers/sortingFunctions/keyInsensitiveComparatorFn";
import { isCategoryWithDimensionId } from "@/components/pages/dimensions/utils/helpers/isCategoryWithDimensionId";
import { CategoryFiltersCountCell } from "@/components/pages/dimensions/components/data-grid/categories/CategoryFiltersCountCell";
import { CategoriesByDimensionIdMap } from "@/store/dimensions/selectors/dimensions-data/categoriesByDimensionIdMapSelector";
import { DimensionActionsCellRenderer } from "@/components/pages/dimensions/components/data-grid/dimensions/cell-renderers/DimensionActionsCellRenderer";
import { CategoryActions } from "@/components/pages/dimensions/components/data-grid/categories/actions/CategoryActions";
import { DimensionNameCellRenderer } from "@/components/pages/dimensions/components/data-grid/dimensions/cell-renderers/DimensionNameCellRenderer";
import { createdByColumnDef } from "@/components/pages/common/ag-grid/components/columns/createdByColumnDef";
import { modifiedByColumnDef } from "@/components/pages/common/ag-grid/components/columns/modifiedByColumnDef";
import { agGridEmptyColumnToFitEmptySpace } from "@/components/pages/common/ag-grid/utils/constants/agGridEmptyColumnToFitEmptySpace";
import { dimensionsDataGridNameValueGetter } from "@/components/pages/dimensions/components/data-grid/dimensions/utils/helpers/dimensionsDataGridNameValueGetter";
import { agGridActionColumnDefaultColumnDef } from "@/components/pages/common/ag-grid/utils/constants/agGridActionColumnDefaultColumnDef";

export const dimensionsAutoGroupColumnDef: ColDef<DimensionsWithChildren> = {
  headerName: "Name",
  field: "name",
  minWidth: 350,
  flex: 1,
  suppressKeyboardEvent: () => true,
  cellRendererParams: {
    suppressCount: true,
    innerRenderer: DimensionNameCellRenderer,
  },
  comparator: keyInsensitiveComparatorFn,
  valueGetter: ({ data }) => {
    return dimensionsDataGridNameValueGetter(data);
  },

  filterValueGetter: ({ data }) => data?.name ?? "",
};

export const getDimensionsGridColDefs = (
  categoriesByDimensionId?: CategoriesByDimensionIdMap,
): ColDef[] => [
  {
    field: "id",
    headerName: "Filters",
    sortable: false,
    filter: false,
    cellRenderer: ({
      data,
      node,
    }: CustomCellRendererProps<
      DimensionsWithChildren | CategoryWithDimensionId
    >) => {
      if (!data?.id) {
        return null;
      }

      if (node.footer) {
        return;
      }

      if (isCategoryWithDimensionId(data)) {
        return (
          <CategoryFiltersCountCell
            category={data}
            dimensionId={data.dimensionId}
            categories={categoriesByDimensionId?.[data.dimensionId] ?? []}
          />
        );
      }

      return <OpenInResourceExplorerButton dimensionId={data.id} />;
    },
  },
  modifiedByColumnDef,
  createdByColumnDef,
  agGridEmptyColumnToFitEmptySpace,

  {
    ...agGridActionColumnDefaultColumnDef,
    colId: "actions",
    maxWidth: 70,
    width: 70,
    minWidth: 70,
    cellDataType: false,

    cellRenderer: ({
      data,
      node,
    }: CustomCellRendererProps<
      DimensionsWithChildren | CategoryWithDimensionId
    >) => {
      if (!data?.id) {
        return;
      }

      if (node.footer) {
        return;
      }

      if (isCategoryWithDimensionId(data)) {
        return (
          <CategoryActions
            category={data}
            dimensionId={data.dimensionId}
            categories={categoriesByDimensionId?.[data.dimensionId] ?? []}
          />
        );
      }

      return <DimensionActionsCellRenderer data={data} />;
    },
  },
];
